import React from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { AuthProvider } from 'react-oidc-context';
import {
  getReturnUrl,
  getSiteSettings,
  setSiteSettings,
} from '../providers/authService';
import App from './App';
import { WebStorageStateStore } from 'oidc-client';
import { initTelemetry, logger } from '../lib/telemetry';
import sendError from '../lib/sendError';
import BootstrapError from './BootstrapError';

// A cached settings blob older than this is refused: a stale unityApiEndpoint would outlive a
// real configuration change and point the app at the wrong environment.
const MAX_CACHED_SETTINGS_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function buildOidcConfig(settings) {
  const { unityIdentityEndpoint, redirect } = settings;
  return {
    authority: unityIdentityEndpoint,
    client_id: 'mcmc-unity-web',
    redirect_uri: redirect,
    scope: 'openid profile offline_access unity_web',
    client_secret: 'QIwoF2KT4SKG72M9MZ0gj1AzxxhjZhSvqAfSMNIvMrg=',
    loadUserInfo: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  };
}

function usableCachedSettings() {
  try {
    const cached = getSiteSettings();
    if (!cached?.unityIdentityEndpoint) return null;

    const cachedAt = Date.parse(cached.cachedAt ?? '');
    if (Number.isNaN(cachedAt)) return null;
    if (Date.now() - cachedAt > MAX_CACHED_SETTINGS_AGE_MS) return null;

    return cached;
  } catch {
    return null;
  }
}

export default function AuthApp() {
  const [oidcConfig, setOidcConfig] = useState(null);
  const [bootstrapFailed, setBootstrapFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function getSettings() {
      try {
        const response = await fetch('settings');
        // A 500 here used to fall through to response.json() throwing, which nothing caught.
        if (!response.ok) {
          throw new Error(`GET /settings responded ${response.status}`);
        }
        const settings = await response.json();
        // setSiteSettings stamps cachedAt, which is what lets the catch below decide whether
        // a previously stored copy is still worth trusting.
        setSiteSettings(settings);
        // Deliberately not awaited: the SDK chunk must not delay first render.
        initTelemetry(settings);
        if (!cancelled) setOidcConfig(buildOidcConfig(settings));
      } catch (error) {
        logger.error(
          'Bootstrap settings fetch failed',
          { Operation: 'Bootstrap' },
          error
        );

        // /settings comes from the same server as the bundle, so a failure here is usually
        // transient. The last good copy is enough to get the user signed in and working.
        const cached = usableCachedSettings();
        if (cancelled) return;

        if (cached) {
          logger.warn('Bootstrapping from cached settings', {
            Operation: 'Bootstrap',
          });
          initTelemetry(cached);
          setOidcConfig(buildOidcConfig(cached));
          return;
        }

        // No cache means no connection string, so initTelemetry no-ops and the logger.error
        // above is stuck in the buffer. POST /Error is the only channel left.
        sendError(error, { Operation: 'Bootstrap' });
        setBootstrapFailed(true);
      }
    }

    getSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSigninCallback = () => {
    window.history.replaceState({}, document.title, window.location.pathname);
    const returnUrl = getReturnUrl();
    if (returnUrl) {
      window.location.replace(returnUrl);
    }
  };

  if (oidcConfig) {
    return (
      <AuthProvider {...oidcConfig} onSigninCallback={onSigninCallback}>
        <App />
      </AuthProvider>
    );
  }

  if (bootstrapFailed) {
    return <BootstrapError />;
  }

  // Was `null`, which made a slow /settings indistinguishable from a crash.
  return <div>Loading...</div>;
}
